import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import api from '../api';

const DAYS = 7;
const box = { border: '1px solid rgba(128,128,128,.35)', borderRadius: 8, padding: 12, marginTop: 10 };
const btn = { padding: '6px 12px', borderRadius: 6, border: '1px solid rgba(128,128,128,.5)', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 13 };
const btnOn = { ...btn, background: '#2563eb', borderColor: '#2563eb', color: '#fff' };
const cell = { padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid rgba(128,128,128,.2)', whiteSpace: 'nowrap', fontSize: 13 };

const nk = s => String(s || '').toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ').trim();
const KW_METRICS = [
  ['Показы', r => r.views],
  ['Клики', r => r.clicks],
  ['CTR, %', r => (r.views > 0 ? (r.clicks * 100 / r.views).toFixed(2) : null)],
  ['Корзины', r => r.baskets],
  ['Заказы', r => r.orders],
  ['Расход, ₽', r => (r.spend != null ? Math.round(r.spend * 100) / 100 : null)],
  ['Ср. поз. (WB)', r => (r.avg_pos != null ? Math.round(r.avg_pos * 10) / 10 : null)],
];

const lastDays = () => {
  const out = [];
  for (let i = 0; i < DAYS; i++) { const d = new Date(Date.now() - i * 86400000); out.push(d.toISOString().slice(0, 10)); }
  return out.reverse();   // слева старые, справа сегодня
};

export default function PositionsPanel({ nmId }) {
  const [kws, setKws] = useState([]);
  const [sel, setSel] = useState({});
  const [hist, setHist] = useState({});
  const [kwStat, setKwStat] = useState({});
  const [open, setOpen] = useState({});
  const [newKw, setNewKw] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [ext, setExt] = useState(false);
  const [busy, setBusy] = useState(null);       // 'organic' | 'ad' | null
  const [prog, setProg] = useState('');
  const [msg, setMsg] = useState('');
  const reqRef = useRef(null);
  const days = lastDays();

  const loadKw = useCallback(async () => {
    const r = await api.get('/positions/keywords', { params: { nmId } });
    setKws(r.data);
    setSel(s => { const n = { ...s }; r.data.forEach(k => { if (n[k.keyword] === undefined) n[k.keyword] = true; }); return n; });
  }, [nmId]);

  const loadHist = useCallback(async () => {
    const r = await api.get('/positions/history', { params: { nmId, days: DAYS + 1 } });
    const m = {};
    r.data.forEach(x => { m[`${x.keyword}|${x.kind}|${x.date}`] = x.position; });
    setHist(m);
    try {
      const k = await api.get('/positions/kw', { params: { nmId, days: DAYS + 1 } });
      const km = {};
      k.data.forEach(x => { km[nk(x.keyword) + '|' + x.date] = x; });
      setKwStat(km);
    } catch (e) { /* реклама по ключам необязательна */ }
  }, [nmId]);

  useEffect(() => { loadKw().catch(e => setMsg(e.message)); loadHist().catch(() => {}); }, [loadKw, loadHist]);

  // связь с расширением
  useEffect(() => {
    const h = e => { if (e.source === window && e.data && e.data.source === 'bystats-ext' && e.data.type === 'pong') setExt(true); };
    window.addEventListener('message', h);
    const ping = () => window.postMessage({ target: 'bystats-ext', type: 'ping' }, '*');
    ping(); const t = setTimeout(ping, 800);
    return () => { window.removeEventListener('message', h); clearTimeout(t); };
  }, []);

  const addKw = async () => {
    const k = newKw.trim();
    if (!k) return;
    try { await api.post('/positions/keywords', { nmId, keyword: k }); setNewKw(''); setMsg(''); await loadKw(); }
    catch (e) { setMsg(e.response?.data?.error || e.message); }
  };
  const delKw = async id => { await api.delete('/positions/keywords/' + id); await loadKw(); };
  const addBulk = async () => {
    const list = bulkText.split(/[\n;,]+/).map(x => x.trim()).filter(Boolean);
    if (!list.length) { setMsg('Список пуст'); return; }
    try {
      const r = await api.post('/positions/keywords/bulk', { nmId, keywords: list });
      const d = r.data;
      setMsg('Добавлено: ' + d.added + (d.duplicates ? ', уже были: ' + d.duplicates : '') + (d.skipped ? ', не поместилось (максимум 20 на артикул): ' + d.skipped : ''));
      setBulkText('');
      if (!d.skipped) setBulkOpen(false);
      await loadKw();
    } catch (e) { setMsg(e.response?.data?.error || e.message); }
  };

  const run = kind => {
    const items = kws.filter(k => sel[k.keyword]).map(k => ({ nmId: Number(nmId), keyword: k.keyword }));
    if (!items.length) { setMsg('Отметь хотя бы один ключ'); return; }
    if (!ext) { setMsg('Расширение не найдено: открой bystats в Chrome с включённым расширением и обнови страницу'); return; }
    const id = 'pos' + Date.now();
    reqRef.current = id;
    const got = [];
    let adSkipped = 0, errors = 0;
    setBusy(kind); setMsg(''); setProg('запуск…');

    const finish = async (text) => {
      window.removeEventListener('message', onMsg);
      setBusy(null); reqRef.current = null;
      setMsg(text);
      await loadHist().catch(() => {});
    };
    const onMsg = async e => {
      const d = e.data;
      if (e.source !== window || !d || d.source !== 'bystats-ext' || d.id !== id) return;
      if (d.type === 'progress') setProg(`проверено ${d.done} из ${d.total}`);
      else if (d.type === 'result') {
        if (d.error) { errors++; return; }
        if (kind === 'organic') {
          if (d.isAd) { adSkipped++; return; }          // с включённой рекламой органику не померить
          got.push({ nmId: d.nmId, keyword: d.keyword, position: d.organic ?? null, page: d.page });
        } else {
          got.push({ nmId: d.nmId, keyword: d.keyword, position: d.position ?? null, page: d.page });
        }
      } else if (d.type === 'error') { await finish('Ошибка расширения: ' + d.error); }
      else if (d.type === 'done') {
        try {
          if (got.length) await api.post('/positions/save', { kind, results: got });
          let t = `Сохранено: ${got.length}`;
          if (adSkipped) t += `. ${adSkipped} шт. пропущено: артикул показан как реклама — для органики выключи рекламу`;
          if (errors) t += `. Не удалось загрузить выдачу: ${errors}`;
          if (d.aborted) t += '. ПРОВЕРКА ОСТАНОВЛЕНА: WB не отдал выдачу, подожди и запусти позже';
          if (d.cancelled) t += ' (остановлено)';
          await finish(t);
        } catch (er) { await finish('Не удалось сохранить: ' + (er.response?.data?.error || er.message)); }
      }
    };
    window.addEventListener('message', onMsg);
    window.postMessage({ target: 'bystats-ext', type: 'check', id, items }, '*');
  };

  const cancel = () => { if (reqRef.current) window.postMessage({ target: 'bystats-ext', type: 'cancel', id: reqRef.current }, '*'); };

  const val = (kw, kind, date) => {
    const k = `${kw}|${kind}|${date}`;
    if (!(k in hist)) return '—';
    return hist[k] == null ? '>500' : hist[k];
  };
  const diff = (kw, date) => {
    const o = hist[`${kw}|organic|${date}`], a = hist[`${kw}|ad|${date}`];
    if (typeof o !== 'number' || typeof a !== 'number') return '';
    const d = o - a;
    return d > 0 ? <span style={{ color: '#16a34a' }}>+{d}</span> : d < 0 ? <span style={{ color: '#dc2626' }}>{d}</span> : '0';
  };
  const fmtD = d => d.slice(8, 10) + '.' + d.slice(5, 7);

  return (
    <div style={box}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button style={busy === 'organic' ? btnOn : btn} disabled={!!busy} onClick={() => run('organic')}>🌿 Проверить органику</button>
        <button style={busy === 'ad' ? btnOn : btn} disabled={!!busy} onClick={() => run('ad')}>📢 Проверить рекламу</button>
        {busy && <button style={btn} onClick={cancel}>Стоп</button>}
        <span style={{ fontSize: 12, opacity: .7 }}>{busy ? prog : (ext ? 'расширение подключено' : 'расширение не найдено')}</span>
      </div>
      {msg && <div style={{ marginTop: 8, fontSize: 13 }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <input value={newKw} onChange={e => setNewKw(e.target.value)} onKeyDown={e => e.key === 'Enter' && addKw()}
          placeholder="новый ключ, например: платье женское" style={{ flex: 1, padding: '6px 8px', borderRadius: 6, border: '1px solid rgba(128,128,128,.5)', background: 'transparent', color: 'inherit', fontSize: 13 }} />
        <button style={btn} onClick={addKw}>Добавить</button>
        <button style={btn} onClick={() => setBulkOpen(o => !o)}>{bulkOpen ? 'Скрыть' : 'Списком'}</button>
      </div>
      {bulkOpen && (
        <div style={{ marginTop: 8 }}>
          <textarea value={bulkText} onChange={e => setBulkText(e.target.value)} rows={6}
            placeholder={'вставь ключи, каждый с новой строки:\nплатье вечернее\nплатье женское\nплатье с открытыми плечами'}
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 6, border: '1px solid rgba(128,128,128,.5)', background: 'transparent', color: 'inherit', fontSize: 13, resize: 'vertical' }} />
          <div style={{ marginTop: 6 }}><button style={btnOn} onClick={addBulk}>Добавить все</button></div>
        </div>
      )}

      {kws.length === 0 ? <div style={{ marginTop: 10, fontSize: 13, opacity: .7 }}>Ключей пока нет — добавь выше (до 20 на артикул)</div> : (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...cell, textAlign: 'left' }} rowSpan={2}>Ключ</th>
                {days.map(d => <th key={d} style={cell} colSpan={3}>{fmtD(d)}</th>)}
              </tr>
              <tr>{days.map(d => [
                <th key={d + 'o'} style={{ ...cell, fontWeight: 400, opacity: .7 }}>орг.</th>,
                <th key={d + 'a'} style={{ ...cell, fontWeight: 400, opacity: .7 }}>рекл.</th>,
                <th key={d + 'd'} style={{ ...cell, fontWeight: 400, opacity: .7 }}>Δ</th>,
              ])}</tr>
            </thead>
            <tbody>
              {kws.map(k => (
                <Fragment key={k.id}>
                <tr>
                  <td style={{ ...cell, textAlign: 'left' }}>
                    <span onClick={() => setOpen(o => ({ ...o, [k.keyword]: !o[k.keyword] }))} title="реклама по ключу за дни" style={{ cursor: 'pointer', marginRight: 6, opacity: .7 }}>{open[k.keyword] ? '▾' : '▸'}</span>
                    <label style={{ cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!sel[k.keyword]} onChange={e => setSel(s => ({ ...s, [k.keyword]: e.target.checked }))} /> {k.keyword}
                      <span title="частота запроса за месяц (WB)" style={{ marginLeft: 8, fontSize: 12, opacity: .65 }}>
                        · {k.freq != null ? Number(k.freq).toLocaleString('ru-RU') : (k.freq_checked ? 'нет данных' : '…')}
                      </span>
                    </label>
                    <span onClick={() => delKw(k.id)} title="удалить ключ" style={{ cursor: 'pointer', marginLeft: 8, opacity: .5 }}>✕</span>
                  </td>
                  {days.map(d => [
                    <td key={d + 'o'} style={cell}>{val(k.keyword, 'organic', d)}</td>,
                    <td key={d + 'a'} style={cell}>{val(k.keyword, 'ad', d)}</td>,
                    <td key={d + 'd'} style={cell}>{diff(k.keyword, d)}</td>,
                  ])}
                </tr>
                {open[k.keyword] && KW_METRICS.map(([label, f]) => (
                  <tr key={k.id + label} style={{ background: 'rgba(59,130,246,0.05)' }}>
                    <td style={{ ...cell, textAlign: 'left', paddingLeft: 26, fontSize: 12, opacity: .85 }}>{label}</td>
                    {days.map(d => {
                      const r = kwStat[nk(k.keyword) + '|' + d];
                      const v = r ? f(r) : null;
                      return <td key={d} colSpan={3} style={{ ...cell, fontSize: 12 }}>{v == null ? '—' : v}</td>;
                    })}
                  </tr>
                ))}
                </Fragment>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 12, opacity: .6, marginTop: 6 }}>Δ = органика − реклама (плюс — реклама подняла выше). «—» — не проверялось, «&gt;500» — не найден в первых 500. Стрелка ▸ у ключа раскрывает рекламную статистику WB по дням (если ключ внесён так же, как называет его WB).</div>
        </div>
      )}
    </div>
  );
}
